@if (isLoading()) {
  <div class="loading">Chargement…</div>
}

<div class="fiche-container animate-fade-in font-sans !my-0">

  <!-- Header -->
  <div class="fiche-titlebar grid grid-cols-3 items-center pt-4 md:pt-8 px-2 md:px-4">
    <div class="justify-self-start">
      <button (click)="goBack()" class="flex items-center gap-2 text-[#BDBDBD] hover:text-primary-600 transition-colors cursor-pointer group">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        <span class="text-[11px] font-bold uppercase tracking-wider">RETOUR</span>
      </button>
    </div>
    <div class="justify-self-center">
      <h1 class="page-title">{{ pageTitle() }}</h1>
    </div>
    <div class="justify-self-end"></div>
  </div>

  @if (error() && !isLoading()) {
    <div class="error">{{ error() }}</div>
  }

  @if (!isLoading() && !error() && documentGroups().length > 0 && entity()) {

    <!-- Level 1: Document type tabs -->
    <div class="overflow-x-auto border-b border-stone-200">
      <div class="fiche-tabs-container !ml-0 !w-max">
        @for (group of documentGroups(); track group.type) {
          <button
            type="button"
            class="fiche-tab"
            [class.active]="activeGroupType() === group.type"
            (click)="selectGroup(group.type)">
            <span class="block">{{ group.name_fr }}</span>
            @if (group.name_ar) {
              <span class="block text-xs opacity-75" dir="rtl">{{ group.name_ar }}</span>
            }
            @if (group.files.length > 1) {
              <span class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-500 text-white text-[10px] font-bold ml-1">{{ group.files.length }}</span>
            }
          </button>
        }
      </div>
    </div>

    <!-- Level 2: File sub-navbar (only when group has multiple files) -->
    @if (activeGroup() && activeGroup()!.files.length > 1) {
      <div class="overflow-x-auto bg-stone-50 border-b border-stone-200 px-4 py-2">
        <div class="flex gap-2 w-max">
          @for (file of activeGroup()!.files; track file.file_id; let i = $index) {
            <button
              type="button"
              (click)="selectFile('' + file.file_id)"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
              [class.bg-primary-600]="activeFileId() === ('' + file.file_id)"
              [class.text-white]="activeFileId() === ('' + file.file_id)"
              [class.bg-white]="activeFileId() !== ('' + file.file_id)"
              [class.text-stone-600]="activeFileId() !== ('' + file.file_id)"
              [class.border]="activeFileId() !== ('' + file.file_id)"
              [class.border-stone-300]="activeFileId() !== ('' + file.file_id)">
              <span class="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold"
                [class.bg-white]="activeFileId() === ('' + file.file_id)"
                [class.text-primary-600]="activeFileId() === ('' + file.file_id)"
                [class.bg-stone-200]="activeFileId() !== ('' + file.file_id)"
                [class.text-stone-500]="activeFileId() !== ('' + file.file_id)">
                {{ i + 1 }}
              </span>
              <span class="max-w-[180px] truncate" [title]="file.file_name">
                @if ((file.file_name || '').length > 22) {
                  {{ (file.file_name || '').substring(0, 14) }}…{{ (file.file_name || '').substring((file.file_name || '').lastIndexOf('.')) }}
                } @else {
                  {{ file.file_name || 'Fichier ' + (i + 1) }}
                }
              </span>
            </button>
          }
        </div>
      </div>
    }

    <!-- File viewer -->
    <div class="fiche-content-area !p-0 overflow-hidden">
      @if (activeFile()) {
        <div class="document-content p-5">

          <!-- File header row -->
          <div class="flex justify-between items-center pb-4 mb-4 border-b border-stone-200">
            <h3 class="text-base font-medium text-stone-800 truncate max-w-lg" [title]="activeFile()!.file_name">
              {{ activeFile()!.file_name || 'Document' }}
            </h3>
            <button
              type="button"
              (click)="downloadActiveFile()"
              class="p-2 text-stone-500 hover:text-stone-800 hover:bg-stone-100 rounded transition-colors flex-shrink-0"
              title="Télécharger">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>
          </div>

          <!-- PDF -->
          @if (activeFileExt() === 'pdf') {
            <iframe
              [src]="safePdfUrl()"
              class="w-full h-[calc(100vh-260px)] border-0 rounded"
              title="Visionneuse PDF">
            </iframe>
          }

          <!-- Image -->
          @else if (['jpg','jpeg','png','gif','webp','bmp'].includes(activeFileExt())) {
            <div class="flex justify-center items-center overflow-auto">
              <img
                [src]="activeFileUrl()"
                [alt]="activeFile()!.file_name"
                class="max-w-full max-h-[calc(100vh-260px)] object-contain rounded shadow" />
            </div>
          }

          <!-- Word (.docx rendered via docx-preview, .doc download only) -->
          @else if (['doc','docx'].includes(activeFileExt())) {
            @if (activeFileExt() === 'docx') {
              <!-- Loading -->
              @if (wordState() === 'loading') {
                <div class="flex flex-col items-center justify-center py-16 gap-3">
                  <div class="w-10 h-10 border-4 border-stone-200 border-t-blue-500 rounded-full animate-spin"></div>
                  <p class="text-stone-500 text-sm">Chargement du document Word...</p>
                </div>
              }
              <!-- Error -->
              @else if (wordState() === 'error') {
                <div class="flex flex-col items-center justify-center py-12 gap-4">
                  <p class="text-red-500 text-sm">{{ wordError() }}</p>
                  <a [href]="activeFileUrl()" [download]="activeFile()!.file_name"
                    class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors">
                    Télécharger à la place
                  </a>
                </div>
              }
              <!-- docx-preview renders here — always in DOM once docx is selected so getElementById works -->
              <div
                class="docx-preview-wrapper overflow-auto bg-stone-100"
                [style.height]="wordState() === 'ready' ? 'calc(100vh - 260px)' : '0'"
                [style.overflow]="wordState() === 'ready' ? 'auto' : 'hidden'">
                <div id="docx-preview-container" class="docx-preview-container"></div>
              </div>
            } @else {
              <!-- .doc not renderable: download only -->
              <div class="flex flex-col items-center justify-center py-12 gap-5">
                <svg class="w-20 h-20" viewBox="0 0 48 48" fill="none">
                  <rect width="48" height="48" rx="6" fill="#2B7CD3"/>
                  <text x="8" y="34" font-size="18" font-weight="bold" fill="white" font-family="Arial">W</text>
                </svg>
                <div class="text-center">
                  <p class="text-stone-700 font-medium text-sm mb-1">{{ activeFile()!.file_name }}</p>
                  <p class="text-stone-400 text-xs mb-5">Le format .doc (ancien) ne peut pas être affiché. Utilisez .docx pour la prévisualisation.</p>
                  <a [href]="activeFileUrl()" [download]="activeFile()!.file_name"
                    class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Télécharger le fichier Word
                  </a>
                </div>
              </div>
            }
          }

          <!-- Excel -->
          @else if (['xls','xlsx'].includes(activeFileExt())) {
            <div class="flex flex-col items-center justify-center py-12 gap-5">
              <svg class="w-20 h-20" viewBox="0 0 48 48" fill="none">
                <rect width="48" height="48" rx="6" fill="#217346"/>
                <text x="8" y="34" font-size="18" font-weight="bold" fill="white" font-family="Arial">X</text>
              </svg>
              <div class="text-center">
                <p class="text-stone-700 font-medium text-sm mb-1">{{ activeFile()!.file_name }}</p>
                <p class="text-stone-400 text-xs mb-5">Ce format ne peut pas être affiché directement dans le navigateur.</p>
                <a [href]="activeFileUrl()" [download]="activeFile()!.file_name"
                  class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Télécharger le fichier Excel
                </a>
              </div>
            </div>
          }

          <!-- PowerPoint -->
          @else if (['ppt','pptx'].includes(activeFileExt())) {
            <div class="flex flex-col items-center justify-center py-12 gap-5">
              <svg class="w-20 h-20" viewBox="0 0 48 48" fill="none">
                <rect width="48" height="48" rx="6" fill="#D24726"/>
                <text x="8" y="34" font-size="18" font-weight="bold" fill="white" font-family="Arial">P</text>
              </svg>
              <div class="text-center">
                <p class="text-stone-700 font-medium text-sm mb-1">{{ activeFile()!.file_name }}</p>
                <p class="text-stone-400 text-xs mb-5">Ce format ne peut pas être affiché directement dans le navigateur.</p>
                <a [href]="activeFileUrl()" [download]="activeFile()!.file_name"
                  class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Télécharger la présentation
                </a>
              </div>
            </div>
          }

          <!-- Other -->
          @else {
            <div class="flex flex-col items-center justify-center py-12 gap-4">
              <svg class="w-16 h-16 text-stone-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p class="text-stone-500 text-sm">Ce fichier ne peut pas être affiché directement.</p>
              <a [href]="activeFileUrl()" [download]="activeFile()!.file_name"
                class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors">
                Télécharger
              </a>
            </div>
          }

        </div>
      }
    </div>
  }

  @if (!isLoading() && documentGroups().length === 0 && !error()) {
    <div class="fiche-content-area">
      <div class="text-center space-y-2">
        <div class="w-16 h-16 bg-stone-50 rounded-full flex items-center justify-center mx-auto text-stone-300">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p class="text-stone-400 font-medium">Aucun document disponible</p>
      </div>
    </div>
  }

</div>
